import { Button } from '@app/components/various/Button';
import { DropdownDrawer } from '@app/components/various/Dropdown/DropdownDrawer';
import { CancelSmall } from '@app/components/various/Icons';
import { Portal } from '@app/components/various/Portal';
import { Scrim } from '@app/components/various/Scrim';
import { SearchFilter } from '@app/components/various/SearchFilter';
import { Filter, Product, ProductFilters } from '@typings';
import { useTranslation } from 'react-i18next';

import { AppliedFilters } from '../AppliedFilters';
import { FiltersSummary } from '../FiltersSummary';
import { StockSwitcher } from '../StockSwitcher';

import { FiltersPanelGroups } from './FiltersPanelGroups';

interface Props {
  isDrawerOpen: boolean;
  onDrawerClose: () => void;
  filters: ProductFilters;
  attributes: Filter.Field<string>[];
  onFilterChange: (newFilters: ProductFilters) => void;
  isLoading: boolean;
  hasActiveFilters: boolean;
  itemsCount: number;
  showAddAll?: boolean;
  allProductsIds?: string[];
  showBarcodeScanner?: boolean;
  showStockSwitcher?: boolean;
  handleToggleFilter: (filterField: string, isExpanded: boolean) => void;
  expandedFilters: Record<string, boolean>;
  onClearFilters: () => void;
  productsDetails?: Record<string, Product.Full>;
}

export const FiltersPanelMobile = ({
  isDrawerOpen,
  onDrawerClose,
  filters,
  attributes,
  onFilterChange,
  isLoading,
  hasActiveFilters,
  itemsCount,
  showAddAll = false,
  allProductsIds,
  showBarcodeScanner,
  showStockSwitcher,
  handleToggleFilter,
  expandedFilters,
  onClearFilters,
  productsDetails,
}: Props) => {
  const { t } = useTranslation(['common']);

  const filtersPanels = (
    <>
      {showStockSwitcher && <StockSwitcher onStockSwitchConfirmation={onDrawerClose} productsDetails={productsDetails} />}
      <SearchFilter
        showBarcodeScanner={showBarcodeScanner}
        onShowBarcodeScanner={onDrawerClose}
        value={filters.search}
        onFilterChange={onFilterChange}
      />
      <FiltersPanelGroups
        attributes={attributes}
        filters={filters}
        expandedFilters={expandedFilters}
        onFilterChange={onFilterChange}
        onToggleFilter={handleToggleFilter}
      />
    </>
  );

  return (
    <Portal>
      <Scrim isVisible={isDrawerOpen} onClick={onDrawerClose} />
      <DropdownDrawer
        isOpen={isDrawerOpen}
        onClose={onDrawerClose}
        title={
          <div className="flex items-center gap-6">
            {t('common:filter_other')}
            {hasActiveFilters && (
              <Button onClick={onClearFilters} icon={CancelSmall} variant="text" color="secondary" className="text-error-45">
                {t('common:reset')}
              </Button>
            )}
          </div>
        }
        className="h-full bg-neutral-96"
      >
        <div className="mb-4 flex flex-wrap gap-1 empty:mb-0">
          <AppliedFilters filters={filters} attributes={attributes} onFilterChange={onFilterChange} isLoading={isLoading} />
        </div>
        <div className="flex flex-col gap-2">
          <FiltersSummary
            allProductsIds={allProductsIds}
            showAddAll={showAddAll && hasActiveFilters}
            itemsCount={itemsCount}
            onAddAll={onDrawerClose}
            filters={filters}
          />
          {filtersPanels}
        </div>
      </DropdownDrawer>
    </Portal>
  );
};
